import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Button,
} from '@mui/material';
import SwaggerUI from 'swagger-ui-react';
import 'swagger-ui-react/swagger-ui.css';
import { RedocStandalone } from 'redoc';
import { Layout } from '../components/Layout';
import { config } from '../config';
import { useDeveloperInfo } from '../hooks/useApi';
import { getOpenApiSpecUrl, getEndpointSlug } from '../utils/docs';
import { isTryOutSlug } from '../tryout/registry';
import type { ApiEndpoint } from '../types';

type ViewerType = 'swagger' | 'redoc' | 'raw';

function DocViewer({
  spec,
  viewer,
  apiKey,
}: {
  spec: object;
  viewer: ViewerType;
  apiKey: string;
}) {
  const requestInterceptor = (req: { headers?: Record<string, string> }) => {
    if (!req.headers) req.headers = {};
    req.headers['x-api-key'] = apiKey;
    return req;
  };

  if (viewer === 'raw') {
    return (
      <Box
        component="pre"
        sx={{
          fontFamily: 'monospace',
          fontSize: '0.875rem',
          backgroundColor: '#f5f5f5',
          padding: 2,
          borderRadius: 1,
          overflow: 'auto',
          margin: 0,
        }}
      >
        {JSON.stringify(spec, null, 2)}
      </Box>
    );
  }

  if (viewer === 'redoc') {
    return (
      <Box sx={{ '& .redoc-wrap': { fontFamily: 'inherit' } }}>
        <RedocStandalone spec={spec} />
      </Box>
    );
  }

  return (
    <Box sx={{ '& .swagger-ui': { fontFamily: 'inherit' } }}>
      <SwaggerUI spec={spec} requestInterceptor={requestInterceptor} />
    </Box>
  );
}

export function DocsPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { data, isLoading: infoLoading, error: infoError } = useDeveloperInfo();
  const [spec, setSpec] = useState<object | null>(null);
  const [specError, setSpecError] = useState<boolean>(false);
  const [specLoading, setSpecLoading] = useState<boolean>(false);
  const [viewer, setViewer] = useState<ViewerType>('swagger');

  const endpoints = (data?.endpoints ?? []) as ApiEndpoint[];
  const selectedEndpoint = useMemo(
    () => endpoints.find((ep) => getEndpointSlug(ep.docsUrl ?? ep.documentation_url ?? '') === slug),
    [endpoints, slug]
  );
  const firstSlug = useMemo(() => {
    const first = endpoints[0];
    return first ? getEndpointSlug(first.docsUrl ?? first.documentation_url ?? '') : '';
  }, [endpoints]);

  // Redirect /docs to /docs/:firstSlug when we have endpoints
  useEffect(() => {
    if (infoLoading || infoError || !data) return;
    if (endpoints.length > 0 && !slug && firstSlug) {
      navigate(`/docs/${firstSlug}`, { replace: true });
    }
  }, [infoLoading, infoError, data, endpoints.length, slug, firstSlug, navigate]);

  // Fetch OpenAPI spec when selected endpoint changes
  useEffect(() => {
    if (!selectedEndpoint || !data?.user.apiKey) {
      setSpec(null);
      setSpecError(false);
      return;
    }
    const specUrl = getOpenApiSpecUrl(
      selectedEndpoint.docsUrl ?? selectedEndpoint.documentation_url ?? '',
      config.apiUrl
    );
    if (!specUrl) {
      setSpec(null);
      setSpecError(true);
      return;
    }
    setSpecLoading(true);
    setSpecError(false);
    setSpec(null);
    fetch(specUrl, {
      headers: { 'x-api-key': data.user.apiKey },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load spec');
        return res.json();
      })
      .then((json) => {
        const apiBase = config.apiUrl.replace(/\/$/, '');
        const rewriteServerUrl = (serverUrl: string): string => {
          try {
            const base = new URL(apiBase);
            const s = new URL(serverUrl, apiBase);
            return base.origin + (s.pathname || '/') + s.search;
          } catch {
            return apiBase;
          }
        };
        const normalizedSpec = {
          ...json,
          servers: Array.isArray(json.servers) && json.servers.length > 0
            ? json.servers.map((s: { url?: string }) => ({
                ...s,
                url: s.url ? rewriteServerUrl(s.url) : apiBase,
              }))
            : [{ url: apiBase }],
        };
        setSpec(normalizedSpec);
        setSpecError(false);
      })
      .catch(() => {
        setSpec(null);
        setSpecError(true);
      })
      .finally(() => setSpecLoading(false));
  }, [selectedEndpoint, data?.user.apiKey]);

  if (infoLoading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  if (infoError) {
    return (
      <Layout>
        <Alert severity="error">Failed to load developer information. Please try again later.</Alert>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout>
        <Alert severity="warning">No data available.</Alert>
      </Layout>
    );
  }

  if (endpoints.length === 0) {
    return (
      <Layout>
        <Alert severity="info">No endpoints are currently available for your account.</Alert>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ display: 'flex', flexDirection: 'row', flex: 1, minHeight: 0, width: '100%', gap: 0 }}>
        <Paper
          elevation={0}
          sx={{
            width: 240,
            flexShrink: 0,
            borderRight: 1,
            borderColor: 'divider',
            overflow: 'auto',
          }}
        >
          <Typography variant="subtitle2" sx={{ px: 2, py: 2 }} color="text.secondary">
            Available Endpoints
          </Typography>
          <List dense disablePadding>
            {endpoints.map((ep) => {
              const epSlug = getEndpointSlug(ep.docsUrl ?? ep.documentation_url ?? '');
              const selected = slug === epSlug;
              return (
                <ListItemButton
                  key={epSlug}
                  component={Link}
                  to={`/docs/${epSlug}`}
                  selected={selected}
                  sx={{ py: 0.5 }}
                >
                  <ListItemText
                    primary={ep.prefix}
                    primaryTypographyProps={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Paper>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {!selectedEndpoint ? (
            <Box sx={{ p: 3 }}>
              <Typography color="text.secondary">Select an endpoint from the list.</Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <ToggleButtonGroup
                  value={viewer}
                  exclusive
                  onChange={(_, v) => v != null && setViewer(v)}
                  size="small"
                >
                  <ToggleButton value="swagger">Swagger UI</ToggleButton>
                  <ToggleButton value="redoc">Redoc</ToggleButton>
                  <ToggleButton value="raw">Raw</ToggleButton>
                </ToggleButtonGroup>
                {slug && isTryOutSlug(slug) && (
                  <Button component={Link} to={`/docs/${slug}/try`} variant="outlined" size="small">
                    Try out
                  </Button>
                )}
              </Box>
              <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                {specLoading && (
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
                    <CircularProgress />
                  </Box>
                )}
                {!specLoading && specError && (
                  <Alert severity="info">No documentation available</Alert>
                )}
                {!specLoading && !specError && spec && (
                  <DocViewer spec={spec} viewer={viewer} apiKey={data.user.apiKey} />
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Layout>
  );
}
